import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { authApi } from '@/features/auth/api/auth.api';
import { Button, PhoneInput } from '@/shared/components/ui';
import { legalConfig } from '@/shared/config/legal';
import { toE164India } from '@/shared/utils/phone';
import { AuthErrorCode } from '@/types';

/**
 * Attach a mobile number to the account you are ALREADY signed in with.
 *
 * This is the repair path for a split account: someone applied with one email
 * and later signed in with a Google address we had never seen, so they landed
 * on a fresh account with no application on it. Their phone is the one thing
 * both accounts share, and an OTP proves they own it.
 *
 * It deliberately does NOT merge accounts. If the number already belongs to
 * someone else, the server says so and we offer the one safe alternative:
 * sign in with that number instead.
 */

interface LinkPhoneDialogProps {
  open: boolean;
  onClose: () => void;
  onLinked: () => void;
  /** Offered when the number turns out to belong to a different account. */
  onSignInWithPhoneInstead: () => void;
}

type Step = 'phone' | 'otp';

function readErrorCode(error: unknown): string | undefined {
  const response = (error as AxiosError<{ code?: string }>)?.response;
  return response?.data?.code;
}

function readErrorMessage(error: unknown, fallback: string): string {
  const response = (error as AxiosError<{ message?: string }>)?.response;
  return response?.data?.message ?? fallback;
}

export function LinkPhoneDialog({
  open,
  onClose,
  onLinked,
  onSignInWithPhoneInstead,
}: LinkPhoneDialogProps) {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  /** Set when the number belongs to another account — a dead end, by design. */
  const [conflict, setConflict] = useState(false);

  const reset = () => {
    setStep('phone');
    setPhone('');
    setOtp('');
    setError('');
    setConflict(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const startMutation = useMutation({
    mutationFn: () =>
      authApi.startPhoneLink(toE164India(phone), {
        smsConsent: true,
        smsConsentVersion: legalConfig.smsConsentVersion,
        smsConsentSource: 'web_link_phone',
      }),
    onSuccess: () => {
      setError('');
      setStep('otp');
    },
    onError: (err) => {
      if (readErrorCode(err) === AuthErrorCode.IDENTITY_CONFLICT) {
        setConflict(true);
        setError('');
        return;
      }
      setError(readErrorMessage(err, 'We could not send a code. Please try again.'));
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => authApi.confirmPhoneLink(toE164India(phone), otp),
    onSuccess: () => {
      reset();
      onLinked();
    },
    onError: (err) => {
      const code = readErrorCode(err);
      if (code === AuthErrorCode.IDENTITY_CONFLICT) {
        setConflict(true);
        setError('');
        return;
      }
      if (code === AuthErrorCode.IDENTITY_ALREADY_LINKED) {
        setError('That number is already on this account.');
        return;
      }
      setError(readErrorMessage(err, 'That code was not correct. Please try again.'));
    },
  });

  const canSend = /^\d{10}$/.test(phone.trim());
  const canConfirm = /^\d{4,8}$/.test(otp.trim());

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Find your application</DialogTitle>

      <DialogContent>
        {conflict ? (
          <Alert severity="warning">
            <AlertTitle sx={{ fontWeight: 700 }}>
              That number belongs to another account
            </AlertTitle>
            Your application is on the account that owns this mobile number. Sign
            in with that number to reach it — we will not combine the two
            accounts automatically. If you believe this is wrong, contact
            support.
          </Alert>
        ) : step === 'phone' ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the mobile number you used on your Guide application. We
              will send a code to confirm it is yours, then link it to this
              account.
            </Typography>
            <PhoneInput
              value={phone}
              onChange={(value: string) => setPhone(value)}
              label="Mobile number"
              autoFocus
            />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1.5 }}
            >
              By continuing you agree to receive a one-time verification SMS.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the code we sent to +91 {phone}.
            </Typography>
            <TextField
              fullWidth
              label="Verification code"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, ''))}
              inputProps={{ inputMode: 'numeric', maxLength: 8 }}
              autoFocus
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {conflict ? (
          <>
            <Button variant="text" onClick={close}>
              Close
            </Button>
            <Button variant="contained" onClick={onSignInWithPhoneInstead}>
              Sign in with that number
            </Button>
          </>
        ) : step === 'phone' ? (
          <>
            <Button variant="text" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canSend || startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              {startMutation.isPending ? 'Sending…' : 'Send code'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="text" onClick={() => setStep('phone')}>
              Back
            </Button>
            <Button
              variant="contained"
              disabled={!canConfirm || confirmMutation.isPending}
              onClick={() => confirmMutation.mutate()}
            >
              {confirmMutation.isPending ? 'Linking…' : 'Link number'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
